import * as THREE from 'three';

const PORTRAITS = [
  'dev-hoodie', 'dev-pixel', 'studio-rep', 'player-refund', 'modder', 'journalist',
  'imposter', 'publisher-exec', 'whistleblower-qa', 'grandma', 'crypto-bro',
  'underground', 'chairman', 'pr-head',
  // Batch 3 roster
  'dev-veteran', 'qa-burnout', 'founder-racer', 'streamer', 'composer',
  'studio-dir', 'server-admin', 'child-player', 'aaa-suit', 'localizer',
];

// PNG kept where runtime keying/multiply needs clean whites; everything else is WebP.
const PROPS: Array<[string, string]> = [
  ['paper-doc', 'webp'], ['desk-texture', 'webp'], ['folder', 'png'], ['badge', 'webp'],
  ['memo', 'webp'], ['scale', 'png'], ['stamp-greenlit', 'png'], ['stamp-sunset', 'png'],
];
const SCENES = [
  'bg-office', 'title-art', 'ending-loyal', 'ending-whistle',
  // Between-day interstitials: the corporation's dead-game aftermath.
  'crowd', 'bg-throne', 'bg-servers', 'bg-graveyard', 'bg-billboards',
];

export class Assets {
  readonly images = new Map<string, HTMLImageElement>();
  readonly textures = new Map<string, THREE.Texture>();

  async loadAll(onProgress?: (done: number, total: number) => void): Promise<void> {
    const entries: Array<[string, string]> = [
      ...PORTRAITS.map((p): [string, string] => [`portrait:${p}`, `assets/art/portraits/${p}.webp`]),
      ...PROPS.map(([p, ext]): [string, string] => [`prop:${p}`, `assets/art/props/${p}.${ext}`]),
      ...SCENES.map((p): [string, string] => [`scene:${p}`, `assets/art/${p}.webp`]),
    ];
    let done = 0;
    await Promise.all(entries.map(async ([key, url]) => {
      const img = new Image();
      img.src = url;
      try {
        await img.decode();
        this.images.set(key, img);
      } catch {
        console.warn(`[assets] failed to load ${url}`);
      }
      done += 1;
      onProgress?.(done, entries.length);
    }));
  }

  image(key: string): HTMLImageElement | undefined {
    return this.images.get(key);
  }

  /** Texture with near-white pixels made transparent (for props shot on white). */
  textureKeyed(key: string): THREE.Texture {
    const cacheKey = `${key}#keyed`;
    let tex = this.textures.get(cacheKey);
    if (!tex) {
      const img = this.images.get(key);
      if (!img) return this.texture(key);
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0);
      const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const px = data.data;
      for (let i = 0; i < px.length; i += 4) {
        const lum = Math.min(px[i], px[i + 1], px[i + 2]);
        if (lum > 232) px[i + 3] = 0;
        else if (lum > 205) px[i + 3] = Math.round(((232 - lum) / 27) * 255);
      }
      ctx.putImageData(data, 0, 0);
      tex = new THREE.CanvasTexture(canvas);
      tex.colorSpace = THREE.SRGBColorSpace;
      this.textures.set(cacheKey, tex);
    }
    return tex;
  }

  texture(key: string): THREE.Texture {
    let tex = this.textures.get(key);
    if (!tex) {
      const img = this.images.get(key);
      tex = img ? new THREE.Texture(img) : new THREE.Texture();
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.needsUpdate = true;
      this.textures.set(key, tex);
    }
    return tex;
  }

  dispose(): void {
    for (const tex of this.textures.values()) tex.dispose();
    this.textures.clear();
  }
}
